/**
 * Background drain for the IndexedDB chunk queue.
 *
 * Runs independently of the MediaRecorder: recording keeps producing chunks
 * into IndexedDB at a steady rate, and this drains them whenever the network
 * allows. The two never block each other, which is the whole point — a 40
 * second outage should cost nothing but a temporarily longer queue.
 *
 * Retry policy: exponential backoff with a ceiling, plus an immediate retry
 * when the browser fires `online`. Chunks are never dropped for failing; the
 * only thing that removes a chunk is a server acknowledgement, or the attempt
 * being long over (`purgeChunksOlderThan`).
 *
 * **The `draining` latch is guarded, and status is published on a heartbeat.**
 * `drain()` returns early while another drain is in flight, and reschedules
 * itself from its own `finally`. So a single await inside the loop that never
 * settles does not merely delay one upload: the latch stays set for the rest
 * of the exam, nothing is ever rescheduled, and no status is ever published
 * again — which leaves `useUploadHealth` reading a stale `pending: 0` and
 * cheerfully reporting "ok" while not one further frame reaches the server.
 * That is how an attempt uploaded four minutes of a twelve-minute exam with no
 * error shown to anyone. The watchdog below is what makes that state
 * self-correcting and, failing that, visible.
 */
import type { RecordingSource } from "@exam/shared";
import { isApiError } from "@/lib/api";
import { uploadChunk } from "@/lib/exam-api";
import {
  listPendingChunks,
  markChunkFailed,
  markChunkRefused,
  removeChunk,
  type PendingChunk,
} from "./chunkStore";

const IDLE_POLL_MS = 3_000;
/**
 * A single chunk cycle — read the queue, upload, delete — cannot legitimately
 * take longer than this: the upload itself is already capped adaptively by the
 * API client, and every IndexedDB call is capped at 15s. Past it, the drain is
 * assumed wedged and the latch is broken so the next attempt can proceed.
 */
const DRAIN_STUCK_MS = 5 * 60_000;
/** How often the watchdog republishes status and checks the latch. */
const WATCHDOG_MS = 10_000;
const BACKOFF_MS = [2_000, 5_000, 10_000, 20_000, 30_000, 60_000];
/** Chunks that keep failing this many times are logged but still retried. */
const NOISY_AFTER_ATTEMPTS = 5;

/**
 * Full jitter on the backoff. Without it, many chunks (or many devices) that
 * failed together retry in lockstep and hammer a recovering uplink at the same
 * instant. Each retry waits a random slice of [0, backoff] instead.
 */
function jittered(ms: number): number {
  return Math.round(ms * (0.5 + Math.random() * 0.5));
}

export interface UploaderStatus {
  pending: number;
  pendingBytes: number;
  uploading: boolean;
  /** Consecutive failures across the queue, not per chunk. */
  failures: number;
  /** Chunks the server has acknowledged this session — the "it IS reaching" count. */
  uploaded: number;
  /** Chunks the server refused permanently — kept on disk, never retried. */
  refused: number;
  lastErrorMessage: string | null;
  lastUploadAtMs: number | null;
  /**
   * The uploader wedged and had to be recovered. Surfaced rather than hidden:
   * every one of these is footage that was at risk, and the student is shown a
   * warning while there is still time to do something about it.
   */
  wedgeRecoveries: number;
}

export interface ChunkUploaderOptions {
  attemptId: string;
  source: RecordingSource;
  /** Required for the phone; omitted on the laptop (session cookie). */
  deviceToken?: string;
  onStatus?: (status: UploaderStatus) => void;
}

export class ChunkUploader {
  private readonly attemptId: string;
  private readonly source: RecordingSource;
  private readonly deviceToken?: string;
  private readonly onStatus?: (status: UploaderStatus) => void;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private draining = false;
  private drainStartedAtMs: number | null = null;
  /**
   * Identifies the drain that currently owns the latch.
   *
   * Breaking the latch does not stop the wedged drain — nothing can, it is
   * blocked on a promise. So when that promise eventually settles, its
   * `finally` runs and would clear the latch belonging to the drain that
   * REPLACED it, blinding the watchdog to a live drain and letting a third
   * start on top. Only the current owner may release.
   */
  private drainRunId = 0;
  private stopped = false;
  private failures = 0;
  private uploaded = 0;
  private wedgeRecoveries = 0;
  private lastErrorMessage: string | null = null;
  private lastUploadAtMs: number | null = null;
  private status: UploaderStatus = {
    pending: 0,
    pendingBytes: 0,
    uploading: false,
    failures: 0,
    uploaded: 0,
    refused: 0,
    lastErrorMessage: null,
    lastUploadAtMs: null,
    wedgeRecoveries: 0,
  };

  private readonly onOnline = () => {
    // Network came back — do not wait out the backoff.
    this.failures = 0;
    this.schedule(0);
  };

  // Returning to a backgrounded tab, or the window regaining focus, is a good
  // moment to retry immediately: mobile browsers throttle/suspend timers in the
  // background, so a queued chunk can otherwise sit undrained until the next
  // poll. We do NOT reset `failures` here — the network may still be poor.
  private readonly onWake = () => {
    if (document.visibilityState === "visible") this.schedule(0);
  };

  constructor(options: ChunkUploaderOptions) {
    this.attemptId = options.attemptId;
    this.source = options.source;
    this.deviceToken = options.deviceToken;
    this.onStatus = options.onStatus;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.stopped = false;
    window.addEventListener("online", this.onOnline);
    document.addEventListener("visibilitychange", this.onWake);
    window.addEventListener("focus", this.onWake);
    this.startWatchdog();
    this.schedule(0);
  }

  /** Stops scheduling. Queued chunks stay on disk and drain on the next start. */
  stop(): void {
    this.stopped = true;
    this.running = false;
    window.removeEventListener("online", this.onOnline);
    document.removeEventListener("visibilitychange", this.onWake);
    window.removeEventListener("focus", this.onWake);
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = null;
  }

  /**
   * Runs on its own interval, independently of the drain loop, and does the
   * two things a wedged drain cannot do for itself: break the latch, and keep
   * publishing status so the UI is never reading a value frozen at the moment
   * things broke.
   */
  private startWatchdog(): void {
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = setInterval(() => {
      if (this.stopped) return;

      if (
        this.draining &&
        this.drainStartedAtMs !== null &&
        Date.now() - this.drainStartedAtMs > DRAIN_STUCK_MS
      ) {
        console.error(
          `[recorder] ${this.source} uploader wedged for ${Math.round(
            (Date.now() - this.drainStartedAtMs) / 1000,
          )}s — breaking the latch and retrying`,
        );
        this.draining = false;
        this.drainStartedAtMs = null;
        // Disowns the wedged drain: its `finally` will see a stale runId.
        this.drainRunId++;
        this.wedgeRecoveries += 1;
        this.failures = 0;
        this.lastErrorMessage = "Upload stalled and was restarted.";
        this.setStatus({
          wedgeRecoveries: this.wedgeRecoveries,
          lastErrorMessage: this.lastErrorMessage,
          failures: 0,
        });
        this.schedule(0);
        return;
      }

      // Republish unconditionally — INCLUDING while a drain is in flight.
      // Publishing only when idle is what let a wedged drain freeze the
      // student's indicator on whatever it happened to say when things broke,
      // which in the failing attempt was a contented "ok". The queue read here
      // is independent of the drain loop, so a hung drain cannot suppress it,
      // and a fresh `lastUploadAtMs` is what lets `useUploadHealth` notice the
      // silence within 45 seconds instead of five minutes.
      void this.publishStatus().catch(() => {
        /* a status read must never break the watchdog */
      });
    }, WATCHDOG_MS);
  }

  /** Drains until the queue is empty or an upload fails. Never rejects. */
  async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    this.drainStartedAtMs = Date.now();
    const runId = ++this.drainRunId;
    this.setStatus({ uploading: true });

    try {
      // Re-read each pass: the recorder may have appended while we uploaded.
      for (;;) {
        if (this.stopped) break;
        // Ownership is checked every pass, not just in the `finally`. A drain
        // the watchdog gave up on is still a running loop: when its hung
        // upload finally settles it would carry on uploading alongside the
        // drain that replaced it, two loops racing over one queue.
        if (runId !== this.drainRunId) break;

        const queue = await this.pending();
        if (queue.length === 0) {
          this.failures = 0;
          this.lastErrorMessage = null;
          break;
        }

        const chunk = queue[0];
        if (!chunk) break;

        const ok = await this.uploadOne(chunk);
        if (!ok) break;
      }
    } catch (err) {
      // Nothing in the loop is expected to throw — uploadOne swallows upload
      // failures — so anything landing here is a storage fault. Recording must
      // continue regardless; the next pass retries.
      console.error(`[recorder] ${this.source} drain failed`, err);
      this.lastErrorMessage =
        err instanceof Error ? err.message : "Upload queue failed";
    } finally {
      // A drain the watchdog already gave up on is a zombie: it must not
      // release a latch it no longer owns, and must not schedule a run on top
      // of the one that replaced it — that blinds the watchdog to a live drain
      // and lets a third start, each holding a socket and a blob.
      if (runId === this.drainRunId) {
        this.draining = false;
        this.drainStartedAtMs = null;
        await this.publishStatus().catch(() => {
          /* a status read must never be the thing that breaks the loop */
        });
        if (!this.stopped) {
          // Full jitter on the backoff so failed chunks don't retry in lockstep.
          this.schedule(
            this.failures === 0
              ? IDLE_POLL_MS
              : jittered(
                  BACKOFF_MS[
                    Math.min(this.failures - 1, BACKOFF_MS.length - 1)
                  ] ?? IDLE_POLL_MS,
                ),
          );
        }
      }
    }
  }

  /* -- internals ---------------------------------------------------- */

  private async uploadOne(chunk: PendingChunk): Promise<boolean> {
    try {
      await uploadChunk({
        attemptId: chunk.attemptId,
        source: chunk.source,
        chunkIndex: chunk.chunkIndex,
        sessionIdx: chunk.sessionIdx ?? 0,
        startedMs: chunk.startedMs,
        durationMs: chunk.durationMs,
        blob: chunk.blob,
        deviceToken: this.deviceToken,
      });

      await removeChunk(chunk.id);
      this.failures = 0;
      this.uploaded += 1;
      this.lastErrorMessage = null;
      this.lastUploadAtMs = Date.now();
      return true;
    } catch (err) {
      // A `conflict` means the attempt closed and the upload window has passed
      // (docs/API.md §5: 5 minutes after submit). That chunk will never be
      // accepted, so retrying forever would pin the queue on it and block
      // every chunk behind it.
      //
      // `forbidden` used to be dropped here too, and must not be: in this API
      // it means AUTHORISATION, not a closed window — "this device is paired
      // to a different attempt", "only the paired phone can upload phone
      // chunks". A reset or a retake moves the laptop to a new attempt id
      // while the phone still holds a token for the old one, and every upload
      // 403s. Dropping on that deletes the phone's entire recording, one chunk
      // per loop iteration, at full speed, irreversibly. It is now retried and
      // reported like any other failure — a 403 that is genuinely permanent
      // costs a stalled queue, which is visible; the alternative cost silence
      // and the footage.
      if (isApiError(err, "conflict")) {
        // Skipped, NOT deleted.
        //
        // The queue must not be pinned on a chunk the server will never
        // accept, but deleting it was the wrong way to achieve that: it is
        // the only irreversible action in the whole pipeline, and it fired
        // exactly when there was most to lose — a slow uplink builds a
        // backlog all exam, then the upload window closes and the client
        // destroys the backlog one chunk at a time, at full speed, silently.
        //
        // Marking it failed takes it out of the way just as effectively. The
        // bytes stay in IndexedDB, where `purgeChunksOlderThan` clears them
        // on its own schedule, and until then they can still be recovered.
        console.warn(
          `[recorder] server refused chunk ${chunk.chunkIndex} (${err.message}) — skipping it`,
        );
        this.lastErrorMessage = err.message;
        await markChunkRefused(chunk);
        return true;
      }

      this.failures += 1;
      this.lastErrorMessage =
        err instanceof Error ? err.message : "Upload failed";
      await markChunkFailed(chunk);

      if (chunk.attempts + 1 === NOISY_AFTER_ATTEMPTS) {
        console.warn(
          `[recorder] chunk ${chunk.chunkIndex} (${this.source}) has failed ${NOISY_AFTER_ATTEMPTS} times — still retrying`,
        );
      }
      return false;
    }
  }

  /** Every chunk on disk for this attempt and source, refused or not. */
  private stored(): Promise<PendingChunk[]> {
    return listPendingChunks(this.attemptId).then((rows) =>
      rows.filter((c) => c.source === this.source),
    );
  }

  /**
   * What is still worth trying to send. Refused chunks are excluded: they are
   * kept on disk but must never be picked again, or the drain — which always
   * takes the oldest queued chunk — would loop on the same one forever.
   */
  private async pending(): Promise<PendingChunk[]> {
    return (await this.stored()).filter((c) => !c.refused);
  }

  private schedule(ms: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.drain(), ms);
  }

  private async publishStatus(): Promise<void> {
    const stored = await this.stored();
    const queue = stored.filter((c) => !c.refused);
    // Counted from DISK, not from a counter on this instance. The flag
    // survives a reload and the counter does not, so an in-memory count made
    // the page forget — after a refresh it reported zero refused, `pending`
    // was zero because refused chunks are excluded from it, and the student
    // was shown "All recording uploaded" over footage that had been rejected.
    this.setStatus({
      pending: queue.length,
      pendingBytes: queue.reduce((sum, c) => sum + c.blob.size, 0),
      refused: stored.length - queue.length,
      // Reflects reality rather than assuming the caller is the drain's
      // `finally`: the watchdog also publishes, mid-drain.
      uploading: this.draining,
      failures: this.failures,
      uploaded: this.uploaded,
      lastErrorMessage: this.lastErrorMessage,
      lastUploadAtMs: this.lastUploadAtMs,
      wedgeRecoveries: this.wedgeRecoveries,
    });
  }

  private setStatus(patch: Partial<UploaderStatus>): void {
    this.status = { ...this.status, ...patch };
    this.onStatus?.(this.status);
  }
}
